import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, from, map, of, shareReplay, switchMap } from 'rxjs';
import { SignedInPerson, signedInPerson } from '../helpers/signed-in-email';
import { getFirebaseBackend } from '../../authUtils';
import { User as AppUser } from 'src/app/store/Authentication/auth.models';

import { getApp } from 'firebase/app';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { environment } from '../../../environments/environment';
import {
  signOut,
  getAuth,
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithCustomToken,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  User as FirebaseUser
} from 'firebase/auth';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private userSubject = new BehaviorSubject<FirebaseUser | null>(null);
  public user$: Observable<FirebaseUser | null> = this.userSubject.asObservable();
  /** The signed-in person and their key (core/helpers/signed-in-email.ts); null when signed out. */
  public signedInPerson$: Observable<SignedInPerson | null> = this.user$.pipe(
    switchMap((user) => (user ? from(signedInPerson(user)) : of(null))),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  user: AppUser;

  constructor() {
    const auth = getAuth();
    onAuthStateChanged(auth, (user) => {
      this.userSubject.next(user);
    });
  }

  /**
   * Returns the current user (from your custom backend service)
   */
  public currentUser(): AppUser {
    return getFirebaseBackend().getAuthenticatedUser(); // adapt this to modular SDK if needed
  }

  /**
   * Performs login.
   *
   * After the password credential resolves we invoke `claimRoles`
   * (the same callable the mobile app calls on every sign-in) so
   * platform super-admins listed in `functions/superAdmins.js` are
   * auto-promoted with the `admin: true` Firebase Auth custom claim.
   * Without this, signing into WebAdmin from a fresh email/password
   * account never picks up the platform super-admin claim — the
   * v1 onUserSignUp trigger doesn't fire for sign-INS, only for
   * sign-UPS.
   *
   * Token refresh: if claimRoles reports `promotedToSuperAdmin: true`,
   * the user's just-issued ID token is stale (the claim was set
   * server-side AFTER the token was minted). We force a refresh via
   * `getIdToken(true)` so the auth.guard's claim short-circuit sees
   * the new claim immediately, no sign-out + sign-in required.
   *
   * Errors from claimRoles are non-fatal — sign-in succeeded, so we
   * still resolve the user. The user just won't have super-admin
   * privileges that round-trip; auth.guard will fall through to its
   * per-event permission check, and they'll see whatever role is in
   * `events_management/{e}/permissions/{md5}`.
   */
  login(email: string, password: string) {
    const auth = getAuth();
    return from(signInWithEmailAndPassword(auth, email, password)).pipe(
      switchMap(async (credential) => {
        try {
          // Two reasons we use the modular SDK's httpsCallable
          // directly instead of @angular/fire's DI-injected one:
          //   1. @angular/fire's Functions instance has its own
          //      auth-state listener that may not have fired yet
          //      when we get here (microtask race vs. signInWith
          //      EmailAndPassword resolving). The result was a 401
          //      "sign in first" because no Bearer token got
          //      attached to the request.
          //   2. Forcing `await credential.user.getIdToken()` BEFORE
          //      the call gives all auth listeners (including the
          //      Functions SDK's internal one) a chance to fire and
          //      caches the token in the user object. By the time
          //      we call httpsCallable, auth.currentUser is settled.
          await credential.user.getIdToken();

          const fns = getFunctions(getApp(), 'europe-west3');
          const callable = httpsCallable<unknown, { promotedToSuperAdmin?: boolean }>(
            fns,
            'claimRoles'
          );
          const result = await callable({});
          if (result.data?.promotedToSuperAdmin) {
            // Force-refresh the ID token (forceRefresh=true) so the
            // newly-set admin: true claim is visible to subsequent
            // requests in this session, no sign-out + sign-in needed.
            await credential.user.getIdToken(true);
          }
        } catch (err) {
          // Non-fatal — log and continue. User keeps the per-event
          // permission grants they already had (if any).
          console.warn('[AuthenticationService] claimRoles failed:', err);
        }
        return credential.user;
      })
      // DO NOT catchError here — let the component handle it
    );
  }

  /**
   * Shared post-sign-in step: invoke `claimRoles` (super-admin auto-promote +
   * convener/speaker grant match) and force an ID-token refresh when a claim
   * was just set. Non-fatal — sign-in already succeeded.
   */
  private async claimRolesAfterSignIn(user: FirebaseUser): Promise<void> {
    try {
      await user.getIdToken();
      const fns = getFunctions(getApp(), 'europe-west3');
      const callable = httpsCallable<unknown, { promotedToSuperAdmin?: boolean }>(fns, 'claimRoles');
      const result = await callable({});
      if (result.data?.promotedToSuperAdmin) await user.getIdToken(true);
    } catch (err) {
      console.warn('[AuthenticationService] claimRoles after sign-in failed:', err);
    }
  }

  /**
   * ORCID / LinkedIn step 1: redirect the browser to the provider's authorize
   * URL. Reuses the mobile bounce redirect (already registered with the
   * provider); a 'wa.'-prefixed `state` tells the bounce page to forward the
   * result to THIS WebAdmin instead of the hepcon:// app scheme. The nonce is
   * stashed for CSRF verification on the callback.
   */
  startOidcSignIn(provider: 'google' | 'orcid' | 'linkedin', returnUrl: string): void {
    const oidc = (environment as any).oidc;
    const cfg = oidc?.[provider];
    if (!cfg) { console.error('[Auth] OIDC not configured for', provider); return; }
    const redirectUri = `https://${oidc.redirectHost}/auth/${provider}/callback`;
    const nonce = this.randomNonce();
    sessionStorage.setItem('oidc_nonce', nonce);
    sessionStorage.setItem('oidc_provider', provider);
    sessionStorage.setItem('oidc_return', returnUrl || '/');
    const qs = new URLSearchParams({
      client_id: cfg.clientId,
      response_type: 'code',
      scope: cfg.scopes,
      redirect_uri: redirectUri,
      state: 'wa.' + nonce,
    });
    window.location.href = `${cfg.authorizeUrl}?${qs.toString()}`;
  }

  /**
   * ORCID / LinkedIn step 2 (on the callback route): exchange the code for a
   * custom token via `exchangeOidcCode` (server-side, holds the client secret),
   * sign in, run claimRoles. redirectUri MUST match the authorize call.
   */
  completeOidcSignIn(provider: 'google' | 'orcid' | 'linkedin', code: string) {
    const oidc = (environment as any).oidc;
    const redirectUri = `https://${oidc.redirectHost}/auth/${provider}/callback`;
    const fns = getFunctions(getApp(), 'europe-west3');
    const callable = httpsCallable<{ provider: string; code: string; redirectUri: string }, { customToken: string }>(
      fns, 'exchangeOidcCode'
    );
    return from(callable({ provider, code, redirectUri })).pipe(
      switchMap(async (res) => {
        const cred = await signInWithCustomToken(getAuth(), res.data.customToken);
        await this.claimRolesAfterSignIn(cred.user);
        return cred.user;
      })
    );
  }

  private randomNonce(): string {
    const a = new Uint8Array(16);
    crypto.getRandomValues(a);
    return Array.from(a).map((b) => b.toString(16).padStart(2, '0')).join('');
  }

  /**
   * Passwordless "HEPCon account" step 1: email a 6-digit sign-in code via the
   * `sendEmailSignInCode` callable (the SAME flow the mobile app uses, so a
   * convener who signed up with a hepcon email account on mobile gets the same
   * uid here). Always resolves ok — never reveals whether an account exists.
   */
  async sendEmailCode(email: string): Promise<void> {
    const fns = getFunctions(getApp(), 'europe-west3');
    const callable = httpsCallable<{ email: string }, { ok: boolean }>(fns, 'sendEmailSignInCode');
    await callable({ email });
  }

  /**
   * Step 2: verify the code via `verifyEmailSignInCode` (returns a custom token
   * for the master account), sign in with it, then run claimRoles. Same uid as
   * the mobile account, so grants/super-admin claim carry over.
   */
  signInWithEmailCode(email: string, code: string) {
    const auth = getAuth();
    const fns = getFunctions(getApp(), 'europe-west3');
    const callable = httpsCallable<{ email: string; code: string }, { customToken: string }>(
      fns, 'verifyEmailSignInCode'
    );
    return from(callable({ email, code })).pipe(
      switchMap(async (res) => {
        const cred = await signInWithCustomToken(auth, res.data.customToken);
        await this.claimRolesAfterSignIn(cred.user);
        return cred.user;
      })
    );
  }

  /**
   * The organizer-console hand-off (2026-10-10): the confirm click on a preview request
   * proved the address, and the confirm page sent the browser to /auth/preview with a
   * one-time code. `redeemConsoleHandoff` burns the code and mints the SAME custom token
   * `verifyEmailSignInCode` would mint for that address, so this session is the account the
   * organizer would get by typing a code. claimRoles then turns the admin permission seeded
   * at preview build into the grant the console reads.
   */
  signInWithConsoleHandoff(code: string) {
    const auth = getAuth();
    const fns = getFunctions(getApp(), 'europe-west3');
    const callable = httpsCallable<{ code: string }, { customToken: string; eventId: string; email: string }>(
      fns, 'redeemConsoleHandoff'
    );
    return from(callable({ code })).pipe(
      switchMap(async (res) => {
        const cred = await signInWithCustomToken(auth, res.data.customToken);
        await this.claimRolesAfterSignIn(cred.user);
        return { user: cred.user, eventId: res.data.eventId };
      })
    );
  }


  /**
   * Performs registration.
   *
   * Same `claimRoles` auto-promotion as login(): if the new user's
   * email is in `functions/superAdmins.js`'s SUPER_ADMIN_EMAILS,
   * the platform `admin: true` Firebase Auth claim gets set on
   * their fresh uid, and we force-refresh the ID token so the
   * dashboard's super-admin short-circuit picks it up immediately.
   * Without this, the very first WebAdmin sign-up by a super-admin
   * would land on an empty dashboard until they sign out + back in.
   */
  register(user: AppUser) {
    const auth = getAuth();
    return from(
      createUserWithEmailAndPassword(auth, user.email, user.password)
    ).pipe(
      switchMap(async (credential) => {
        try {
          // Same race-mitigation as login() — await getIdToken first to
          // ensure auth state has fully propagated to the Functions SDK
          // before the callable invocation.
          await credential.user.getIdToken();
          const fns = getFunctions(getApp(), 'europe-west3');
          const callable = httpsCallable<unknown, { promotedToSuperAdmin?: boolean }>(
            fns,
            'claimRoles'
          );
          const result = await callable({});
          if (result.data?.promotedToSuperAdmin) {
            await credential.user.getIdToken(true);
          }
        } catch (err) {
          console.warn('[AuthenticationService] claimRoles after register failed:', err);
        }
        return credential.user;
      })
    );
  }

  /**
   * Sends password reset email
   */
  resetPassword(email: string) {
    const auth = getAuth();
    return from(sendPasswordResetEmail(auth, email));
  }

  /**
   * Logs out the current user
   */
  logout(): Observable<void> {
    return from(
      signOut(getAuth()).then(() => {
        sessionStorage.clear();
        localStorage.clear();
      })
    );
  }
}
